import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Check, Zap, Globe, Share2, Shield, Lock, ChevronRight } from "lucide-react";

const benefits = [
  { icon: Globe, text: "Listed in the scout directory" },
  { icon: Share2, text: "Shareable profile link works publicly" },
  { icon: Zap, text: "Visible to verified scouts and clubs" },
  { icon: Shield, text: "14-day no-questions refund" },
];

const plans = [
  {
    id: "pro", name: "FULL ACCESS", price: 19, popular: true,
    features: ["Structured Player Profile", "Public Profile Page", "Listed in Network Directory", "Reviewed by Verified Scouts", "Shareable Profile Link", "Video Highlights", "Unlimited Media"],
  },
];

export default function Paywall() {
  const { user, register: authRegister } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isLoggedIn = user && user !== false;

  const handlePay = async () => {
    setLoading(true);
    setError("");
    try {
      if (!isLoggedIn) {
        if (!name.trim() || !email.trim() || !password) {
          setError("Please fill in all fields");
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setError("Password must be at least 6 characters");
          setLoading(false);
          return;
        }
        const res = await authRegister(email, password, name);
        if (!res.success) {
          setError(res.error);
          setLoading(false);
          return;
        }
      }
      const origin = window.location.origin;
      const { data } = await api.post("/payments/create-checkout", { plan: selectedPlan, origin_url: origin });
      if (data.url) {
        // Meta Pixel — InitiateCheckout
        if (typeof window !== "undefined" && window.fbq) {
          window.fbq("track", "InitiateCheckout", {
            content_name: "Elite Scouting Network — Profile Access",
            content_category: "football_profile",
            value: 19.00,
            currency: "USD",
          });
        }
        window.location.href = data.url;
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  // Allow skip for logged-in users with existing plans
  const skipToDashboard = () => navigate("/profile/edit");

  // Single-tier $19 model — no upgrade flow needed.
  // If user is already on a plan, send them straight to the editor.
  useEffect(() => {
    if (user?.plan) navigate("/profile/edit");
  }, [user?.plan, navigate]);

  return (
    <div className="bg-[#0A0A0A] min-h-screen flex items-center justify-center px-4 py-12" data-testid="paywall-page">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <Lock size={28} className="text-[#D4AF37] mx-auto mb-3" />
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-2" data-testid="paywall-headline">
            Publish to the scout directory.
          </h1>
          <p className="text-sm text-[#A3A3A3] max-w-md mx-auto">
            <span className="text-[#D4AF37] font-semibold">$19 · one-off payment</span> · Your profile goes live, scouts can find you, and your shareable link unlocks instantly.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-center gap-3 bg-[#141414] border border-[#262626] p-3 rounded-sm">
              <b.icon size={16} className="text-[#D4AF37] flex-shrink-0" />
              <span className="text-xs text-[#E5E5E5]">{b.text}</span>
            </div>
          ))}
        </div>

        {/* Single $19 Full Access plan */}
        <div className="grid grid-cols-1 gap-3 mb-6">
          {plans.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`p-5 border rounded-sm transition-all text-left relative ${
                selectedPlan === plan.id
                  ? "border-[#D4AF37] bg-[#141414]"
                  : "border-[#262626] bg-[#0E0E0E] hover:border-[#D4AF37]/40"
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && <span className="absolute -top-2.5 left-3 badge-gold text-[8px]">RECOMMENDED</span>}
              <div className="overline text-[10px] mb-1">{plan.name}</div>
              <div className="text-2xl font-black text-[#F5F5F5] mb-2">${plan.price}<span className="text-xs font-normal text-[#888]"> one-off</span></div>
              <ul className="space-y-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="text-[10px] text-[#A3A3A3] flex items-start gap-1">
                    <Check size={8} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        {/* Register (if not logged in) */}
        {!isLoggedIn && (
          <div className="card-dark p-5 mb-5 space-y-3" data-testid="paywall-register">
            <div className="overline text-[10px]">CREATE ACCOUNT</div>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-dark text-sm py-2.5" placeholder="Full name" data-testid="paywall-name" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark text-sm py-2.5" placeholder="Email" data-testid="paywall-email" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark text-sm py-2.5" placeholder="Password (min 6 chars)" data-testid="paywall-password" />
          </div>
        )}

        {error && (
          <div className="text-red-400 text-xs bg-red-400/10 border border-red-400/20 px-3 py-2 rounded-sm mb-4" data-testid="paywall-error">{error}</div>
        )}

        <button
          onClick={handlePay}
          disabled={loading}
          className="btn-gold w-full text-base py-4 flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="paywall-pay-btn"
        >
          {loading ? (
            <><div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> PROCESSING...</>
          ) : (
            <>GET MY PROFILE &mdash; ${plans.find((p) => p.id === selectedPlan)?.price} <ChevronRight size={18} /></>
          )}
        </button>
        <p className="text-center text-[10px] text-[#A3A3A3] mt-3">One-off payment. No subscription. Secure via Stripe.</p>
        <p className="text-center text-xs text-[#E5E5E5] mt-5" data-testid="paywall-accessibility-line">Built to be accessible to players everywhere.</p>
        <p className="text-center text-[10px] text-[#777] mt-1.5" data-testid="paywall-value-line">Less than what most players spend on trials, boots, or travel.</p>

        {isLoggedIn && user?.plan && (
          <button onClick={skipToDashboard} className="w-full text-center text-xs text-[#D4AF37] hover:underline mt-4" data-testid="skip-to-onboarding">
            I already have a plan — go to onboarding
          </button>
        )}
      </div>
    </div>
  );
}
